import React, { useMemo, useState } from 'react';
import { useAdmin, useDebounced } from '../context';
import { Icon } from '../icons';
import { CapId, StaffMemberView, StaffState } from '../types';
import { Empty } from '../ui';
import { CapState, cellState, grantable, roleOf, statusOf, withCapState } from './helpers';
import { RoleChip } from './parts';

const SHORT: Record<string, string> = {
  players: 'Joueurs', teleport: 'Téléport.', modes: 'Modes', items: 'Objets', kick: 'Expuls.', ban: 'Bannis.', broadcast: 'Annonces', world: 'Monde',
  restart: 'Redém.', weather: 'Météo', reports: 'Signal.', housing: 'Logem.', debug: 'Debug', console: 'Cmd',
};

const CELL_TITLE: Record<string, string> = {
  'inherit-on': 'Hérité du rôle : autorisé',
  add: 'Ajouté en plus du rôle',
  deny: 'Retiré par rapport au rôle',
  off: 'Non autorisé',
};

export const MatrixTab: React.FC<{ staff: StaffState; members: StaffMemberView[]; patchLocal: (discordId: string, patch: Partial<StaffMemberView>) => void }> = ({ staff, members, patchLocal }) => {
  const { send } = useAdmin();
  const [search, setSearch] = useState('');
  const dSearch = useDebounced(search, 120);
  const cols = useMemo(() => grantable(staff), [staff]);

  const rows = useMemo(() => {
    const q = dSearch.trim().toLowerCase();
    return members
      .filter((m) => !q || [m.name, m.discordId].join(' ').toLowerCase().indexOf(q) !== -1)
      .sort((a, b) => (a.name || a.discordId).localeCompare(b.name || b.discordId, 'fr', { sensitivity: 'base' }));
  }, [members, dSearch]);

  const totals = useMemo(() => {
    const t: Record<string, number> = {};
    for (const c of cols) t[c.id] = 0;
    for (const m of rows) {
      if (statusOf(m) !== 'active') continue;
      const role = roleOf(staff, m.roleId);
      for (const c of cols) {
        const s = cellState(role, m.caps, m.denied, c.id);
        if (s === 'inherit-on' || s === 'add') t[c.id]++;
      }
    }
    return t;
  }, [rows, cols, staff]);

  const activeCount = rows.filter((m) => statusOf(m) === 'active').length;

  const toggle = (m: StaffMemberView, cap: CapId) => {
    const role = roleOf(staff, m.roleId);
    const state = cellState(role, m.caps, m.denied, cap);
    const inRole = !!role && role.caps.indexOf(cap) !== -1;
    const on = state === 'inherit-on' || state === 'add';
    // On: take it away (deny when the role gives it, else drop the addition). Off: give it back (drop the denial, else add it)
    const next: CapState = on ? (inRole ? 'deny' : 'inherit') : inRole ? 'inherit' : 'add';
    const n = withCapState(role, m.caps, m.denied, cap, next);
    patchLocal(m.discordId, { caps: n.caps, denied: n.denied });
    send({ action: 'adminStaffUpsert', discordId: m.discordId, caps: n.caps, denied: n.denied });
  };

  if (members.length === 0) {
    return <Empty title="Aucun membre à afficher" text="Ajoutez des membres dans l'onglet Membres pour voir leurs permissions côte à côte." />;
  }

  return (
    <div className="admin-panel__matrixtab">
      <div className="admin-panel__toolbar">
        <div className="admin-panel__searchfield admin-panel__input--grow">
          <Icon name="search" size={15} className="admin-panel__searchfield-icon" />
          <input className="admin-panel__input admin-panel__input--block" type="search" placeholder="Filtrer les membres" aria-label="Filtrer les membres de la matrice" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <ul className="admin-panel__legend" aria-label="Légende">
          <li><span className="admin-panel__mx admin-panel__mx--inherit-on"><Icon name="check" size={12} /></span> Hérité du rôle</li>
          <li><span className="admin-panel__mx admin-panel__mx--add"><Icon name="plus" size={12} /></span> Ajouté</li>
          <li><span className="admin-panel__mx admin-panel__mx--deny"><Icon name="minus" size={12} /></span> Retiré</li>
          <li><span className="admin-panel__mx admin-panel__mx--off" /> Non autorisé</li>
        </ul>
      </div>
      <p className="admin-panel__hint">Chaque case est l'état final. Un clic accorde ou retire la permission tout de suite, sans toucher au rôle. Les lignes grisées sont suspendues ou expirées.</p>

      <div className="admin-panel__tablewrap admin-panel__tablewrap--matrix">
        <table className="admin-panel__mtable">
          <thead>
            <tr>
              <th className="admin-panel__mtable-first">Membre</th>
              {cols.map((c) => (
                <th key={c.id} title={c.label}>{SHORT[c.id] || c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const role = roleOf(staff, m.roleId);
              const dim = statusOf(m) !== 'active';
              return (
                <tr key={m.discordId} className={dim ? 'is-dim' : ''}>
                  <th scope="row" className="admin-panel__mtable-first">
                    <span className="admin-panel__mname">{m.name || m.discordId}</span>
                    <RoleChip role={role} empty="Aucun rôle" />
                  </th>
                  {cols.map((c) => {
                    const s = cellState(role, m.caps, m.denied, c.id);
                    const on = s === 'inherit-on' || s === 'add';
                    return (
                      <td key={c.id}>
                        <button
                          type="button"
                          className={'admin-panel__mx admin-panel__mx--' + s}
                          aria-pressed={on}
                          aria-label={`${c.label} pour ${m.name || m.discordId} : ${CELL_TITLE[s]}`}
                          title={CELL_TITLE[s]}
                          onClick={() => toggle(m, c.id)}
                        >
                          {s === 'add' ? <Icon name="plus" size={12} /> : s === 'deny' ? <Icon name="minus" size={12} /> : on ? <Icon name="check" size={12} /> : null}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th className="admin-panel__mtable-first">Total ({activeCount} {activeCount > 1 ? 'actifs' : 'actif'})</th>
              {cols.map((c) => (
                <td key={c.id} className="admin-panel__mtable-total">{totals[c.id]}</td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
